/**
 * entry-links.ts — the links the ways into the game are opened by, read in
 * one place: which way in a link names, by its mode; what each way in needs
 * from it and — of a link cut short — what it lacks (readLink); the test
 * player a link names; and the session it names, under which the tab
 * remembers that session's end (domains/session-ended.ts). The router
 * (components/login/login-screen.tsx) and every way in read the page's link
 * here; the language a link names is read before the first paint, by
 * i18n/config.ts (linkLanguage).
 */

/** The page's own link, its query: none outside a browser (the unit tests). */
export function pageSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/** The ways in a link can name, by its mode — the gallery a development build's only. */
const MODES = ['admin-launch', 'session-link', 'moderator-join', 'student-join', 'test-session', 'ui-gallery'] as const;

/** A way in, by the mode its link names. */
type EntryMode = (typeof MODES)[number];

/** The way in a link names, or null for a link that names none: the landing's. */
export function linkMode(search: string): EntryMode | null {
  const mode = new URLSearchParams(search).get('mode');
  return MODES.find((known) => known === mode) ?? null;
}

/**
 * The session a way in's link names: a session's own id (the student join, a
 * session link), or a staff link's match (the admin launch, the moderator's
 * join). Null for a link that names neither — the test session's, whose
 * session comes from the team code.
 */
export function linkSession(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get('session') || params.get('match') || null;
}

/**
 * What each way in needs from its link, and what it may name besides, by the
 * link's own names — the names a link cut short is told it lacks, in this
 * order. The portal builds them all.
 */
const LINK_PARTS = {
  // A student's join: their own player, their class and session (the portal's launchForStudent).
  'student-join': { needs: ['user', 'name', 'cohort', 'world', 'session'], may: [] },
  // An administrator's session link: the session, the name to play under, and
  // the class, without which the server takes no student (the account step).
  'session-link': { needs: ['session', 'name', 'cohort'], may: [] },
  // The portal's admin launch: a test player, in the match it launched.
  'admin-launch': { needs: ['world', 'cohort', 'name', 'match'], may: [] },
  // A moderator's join, from the portal's moderation page: who, the team, the world and its match.
  'moderator-join': { needs: ['user', 'cohort', 'world', 'match'], may: ['name'] },
} as const satisfies Record<string, { needs: readonly string[]; may: readonly string[] }>;

/** A way in that reads parts from its link. */
type LinkedMode = keyof typeof LINK_PARTS;
type Needs<M extends LinkedMode> = (typeof LINK_PARTS)[M]['needs'][number];
type May<M extends LinkedMode> = (typeof LINK_PARTS)[M]['may'][number];

/** A way in's link, read: its parts by name — or, cut short, what it lacks of those it needs. */
type EntryLink<M extends LinkedMode> =
  | { parts: { [K in Needs<M>]: string } & { [K in May<M>]?: string }; missing?: never }
  | { parts?: never; missing: readonly Needs<M>[] };

/** The parts a way in reads from its link, or what the link lacks of those it needs. */
export function readLink<M extends LinkedMode>(search: string, mode: M): EntryLink<M> {
  const params = new URLSearchParams(search);
  const { needs, may } = LINK_PARTS[mode] as { needs: readonly Needs<M>[]; may: readonly May<M>[] };
  const missing = needs.filter((name) => !params.get(name));
  if (missing.length > 0) return { missing };
  const named = [...needs, ...may].flatMap((name) => {
    const value = params.get(name);
    return value ? [[name, value] as const] : [];
  });
  return { parts: Object.fromEntries(named) } as EntryLink<M>;
}

/**
 * A test player's Nakama id — the class and the name, the name lower-cased
 * with every run of other characters a hyphen. The admin launch, a session
 * link and the test session sign their player in under it (the auth service
 * derives it from the class and the name each join is given), and the scripts
 * seat theirs (scripts/lib/nakama-seat.mjs). The portal names each of its test
 * personas by the same rule, buildSyntheticCustomId in portal-backend's
 * admin-launch.service.ts, so a persona's id is the account its launch plays:
 * the two take the same cases from test-player-id-cases.json, this in
 * entry-links.test.ts.
 */
export function testPlayerId(cohort: string, name: string): string {
  return `test_${cohort}_${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/** A match's id as a staff link's card shows it: its first eight characters, enough to tell two apart. */
export function shortMatch(matchId: string): string {
  return `${matchId.slice(0, 8)}…`;
}
